import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import type { z } from 'zod'
import { DeadlineTypeSchema, DeadlineStatusSchema } from '@aegis/zod-schemas'
import { apiFetch } from '@/lib/api-client'

// ── Types (derived from @aegis/zod-schemas) ──────────────────────────────────

export type DeadlineType = z.infer<typeof DeadlineTypeSchema>
export type DeadlineStatus = z.infer<typeof DeadlineStatusSchema>

export interface Deadline {
  id: string
  amlCaseId: string
  type: DeadlineType
  dueDate: string
  status: DeadlineStatus
  assignedTo?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
  // included via amlCase relation
  amlCase?: {
    id: string
    clientId: string
    engagementType: string
    client?: { type?: string; companyName?: string | null; firstName?: string | null; lastName?: string | null } | null
  }
}

export type DeadlineSortKey = 'type' | 'caseClient' | 'dueDate' | 'status' | 'assignee'
export type SortDir = 'asc' | 'desc'

export interface DeadlinesFilters {
  status?: DeadlineStatus
  type?: DeadlineType | 'all'
  amlCaseId?: string
  assignedTo?: string
  dueBefore?: string
  search?: string
  sortBy?: DeadlineSortKey
  sortDir?: SortDir
  page?: number
  limit?: number
}

interface OffsetPage<T> {
  data: T[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useDeadlines(filters?: DeadlinesFilters) {
  return useQuery<OffsetPage<Deadline>>({
    queryKey: ['deadlines', filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.status) params.set('status', filters.status)
      if (filters?.type && filters.type !== 'all') params.set('type', filters.type)
      if (filters?.amlCaseId) params.set('amlCaseId', filters.amlCaseId)
      if (filters?.assignedTo && filters.assignedTo !== 'all') params.set('assignedTo', filters.assignedTo)
      if (filters?.dueBefore) params.set('dueBefore', filters.dueBefore)
      if (filters?.search && filters.search.trim().length >= 2) params.set('search', filters.search.trim())
      if (filters?.sortBy) params.set('sortBy', filters.sortBy)
      if (filters?.sortDir) params.set('sortDir', filters.sortDir)
      params.set('page', String(filters?.page ?? 1))
      params.set('limit', String(filters?.limit ?? 100))
      const qs = params.toString()
      return apiFetch<OffsetPage<Deadline>>(`/deadlines?${qs}`)
    },
  })
}

/** Server-side deadline stats (counts only, no data transfer) */
export interface DeadlineStats {
  overdue: number
  next7: number
  next30: number
  upcoming: number
  completed: number
}

export function useDeadlineStatsServer(enabled = true) {
  return useQuery<DeadlineStats>({
    queryKey: ['deadlines', 'stats'],
    queryFn: () => apiFetch<DeadlineStats>('/deadlines/stats'),
    staleTime: 15_000,
    enabled,
  })
}

/** All overdue deadlines (no pagination, for dashboard) */
export function useOverdueDeadlines() {
  return useQuery<Deadline[]>({
    queryKey: ['deadlines', 'overdue'],
    queryFn: () => apiFetch<Deadline[]>('/deadlines/overdue'),
  })
}

/** Upcoming deadlines within 30 days (no pagination, for dashboard) */
export function useUpcomingDeadlines() {
  return useQuery<Deadline[]>({
    queryKey: ['deadlines', 'upcoming'],
    queryFn: () => apiFetch<Deadline[]>('/deadlines/upcoming'),
  })
}

/** Dashboard: active deadlines (OVERDUE + UPCOMING) from dedicated backend query */
export function useDashboardDeadlines(limit = 20) {
  return useQuery<Deadline[]>({
    queryKey: ['deadlines', 'dashboard', limit],
    queryFn: () => apiFetch<Deadline[]>(`/deadlines/dashboard?limit=${limit}`),
    staleTime: 30_000,
  })
}

/** Backend endpoint: POST /deadlines/case/:caseId (caseId in URL path, not body) */
export function useCreateDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, data }: { caseId: string; data: Record<string, unknown> }) =>
      apiFetch<Deadline>(`/deadlines/case/${caseId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: async () => {
      // refetchType:'all' forza anche le query inattive (es. stats) a ricaricare
      // subito; senza questo la lista principale a volte non mostra la nuova
      // scadenza finché non si cambia filtro.
      await queryClient.invalidateQueries({ queryKey: ['deadlines'], refetchType: 'all' })
    },
  })
}

export function useUpdateDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch<Deadline>(`/deadlines/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deadlines'] })
    },
  })
}

/** Compute stats from a deadlines array (pure function) */
export function computeDeadlineStats(deadlines: Deadline[]) {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const in7 = new Date(now)
  in7.setDate(in7.getDate() + 7)
  const in30 = new Date(now)
  in30.setDate(in30.getDate() + 30)

  return {
    overdue: deadlines.filter((d) => d.status === 'OVERDUE').length,
    next7: deadlines.filter((d) => {
      if (d.status !== 'UPCOMING') return false
      return new Date(d.dueDate) <= in7
    }).length,
    next30: deadlines.filter((d) => {
      if (d.status !== 'UPCOMING') return false
      return new Date(d.dueDate) <= in30
    }).length,
    completed: deadlines.filter((d) => d.status === 'COMPLETED').length,
  }
}

/** Hook wrapper: fetches all deadlines and derives stats */
export function useDeadlineStats(filters?: DeadlinesFilters) {
  const { data } = useDeadlines(filters)
  return computeDeadlineStats(data?.data ?? [])
}
